import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { MagnifyingGlass, Plus, X, Trophy, CaretLeft } from '@phosphor-icons/react';
import GoalsPanel from '../components/GoalsPanel';
import ScrollFadeRow from '../components/ScrollFadeRow';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const EQUIPMENT_TYPES = ['Barbell', 'Dumbbell', 'Machine', 'Cable', 'Bodyweight', 'Cardio - Treadmill', 'Cardio - Outdoor', 'Cardio - Cycle', 'Cardio - Rowing', 'Cardio - Elliptical', 'Kettlebell', 'Band'];
const MUSCLE_GROUPS = ['Chest', 'Shoulders', 'Back', 'Legs', 'Hamstrings', 'Glutes', 'Calves', 'Biceps', 'Triceps', 'Core', 'Forearms', 'Cardio'];

export default function Exercises() {
  const [exercises, setExercises] = useState([]);
  const [pbs, setPbs] = useState({});
  const [q, setQ] = useState('');
  const [muscleFilter, setMuscleFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');   // 'all' | 'weights' | 'cardio' | 'bodyweight'
  const [selected, setSelected] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [ex, pb] = await Promise.all([
        axios.get(`${API_URL}/exercises`, { withCredentials: true }),
        axios.get(`${API_URL}/progress/pbs`, { withCredentials: true }),
      ]);
      setExercises(ex.data);
      const map = {};
      pb.data.forEach((p) => { map[p.exercise_id] = p; });
      setPbs(map);
    } finally { setLoading(false); }
  };

  const inTypeCategory = (ex, cat) => {
    if (cat === 'all') return true;
    const eq = ex.equipment_type || '';
    const mg = ex.muscle_group || '';
    if (cat === 'cardio') return mg === 'Cardio' || eq.startsWith('Cardio');
    if (cat === 'bodyweight') return eq === 'Bodyweight';
    // weights = everything else non-cardio, non-bodyweight
    return !(mg === 'Cardio' || eq.startsWith('Cardio')) && eq !== 'Bodyweight';
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return exercises.filter((e) => {
      if (!inTypeCategory(e, typeFilter)) return false;
      if (muscleFilter !== 'all' && e.muscle_group !== muscleFilter) return false;
      if (!s) return true;
      return e.name.toLowerCase().includes(s)
        || e.muscle_group.toLowerCase().includes(s)
        || (e.equipment_type || '').toLowerCase().includes(s);
    });
  }, [q, muscleFilter, typeFilter, exercises]);

  // Muscle chips reflect what's actually present in the currently-typed filter category
  const muscleOptions = useMemo(() => {
    const seen = new Map(); // lowercase key → canonical display name
    exercises.forEach((e) => {
      if (!inTypeCategory(e, typeFilter)) return;
      const mg = (e.muscle_group || '').trim();
      if (!mg) return;
      const key = mg.toLowerCase();
      if (!seen.has(key)) seen.set(key, mg);
    });
    return Array.from(seen.values()).sort((a, b) => {
      const ia = MUSCLE_GROUPS.indexOf(a);
      const ib = MUSCLE_GROUPS.indexOf(b);
      // Known groups first, in their canonical order; then alphabetic for the rest.
      if (ia !== -1 && ib === -1) return -1;
      if (ib !== -1 && ia === -1) return 1;
      if (ia !== -1 && ib !== -1) return ia - ib;
      return a.localeCompare(b);
    });
  }, [exercises, typeFilter]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((e) => {
      (g[e.muscle_group] = g[e.muscle_group] || []).push(e);
    });
    return g;
  }, [filtered]);

  const createCustom = async (payload) => {
    try {
      const { data } = await axios.post(`${API_URL}/exercises`, payload, { withCredentials: true });
      setExercises((prev) => [...prev, data]);
      setShowCustom(false);
    } catch (e) { alert('Failed to create'); }
  };

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <div className="animate-pulse-soft text-6xl font-black font-heading text-graphite">B</div>
      </div>
    );
  }

  if (selected) {
    return <ExerciseDetail exercise={selected} pb={pbs[selected.id]} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="mobile-container min-h-screen">
      <div className="content-wrapper px-5 pt-10">
        <div className="mb-4 animate-slide-up flex items-end justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-graphite/50 font-semibold mb-1">
              Library
            </p>
            <h1 className="text-5xl font-black font-heading uppercase tracking-tight text-graphite leading-none">
              Exercises
            </h1>
          </div>
          <button
            onClick={() => setShowCustom(true)}
            className="h-12 px-4 bg-userRed text-bone font-bold text-xs uppercase tracking-[0.15em] rounded-ios-pill flex items-center gap-1.5"
            data-testid="new-exercise-button"
          >
            <Plus size={16} weight="bold" />
            New
          </button>
        </div>

        <div className="relative mb-3">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-graphite/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search exercises"
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoCapitalize="none"
            autoCorrect="off"
            className="w-full h-12 pl-10 pr-4 rounded-ios-pill border border-hairline bg-boneWarm focus:outline-none focus:ring-2 focus:ring-graphite text-base"
            data-testid="exercise-library-search"
          />
        </div>

        {/* Type category filter */}
        <ScrollFadeRow className="flex gap-1.5 mb-2" testId="type-filter-chips">
          {[
            { k: 'all', l: 'All' },
            { k: 'weights', l: 'Weights' },
            { k: 'cardio', l: 'Cardio' },
            { k: 'bodyweight', l: 'Bodyweight' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => { setTypeFilter(t.k); setMuscleFilter('all'); }}
              className={`h-9 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.18em] font-bold whitespace-nowrap flex-shrink-0 transition-all ${
                typeFilter === t.k ? 'bg-graphite text-bone' : 'bg-graphite/5 text-graphite/60 hover:bg-graphite/10'
              }`}
              data-testid={`type-chip-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </ScrollFadeRow>

        {/* Muscle group chips */}
        <ScrollFadeRow className="flex gap-1.5 mb-4 pb-1" testId="muscle-filter-chips">
          <button
            onClick={() => setMuscleFilter('all')}
            className={`h-8 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold whitespace-nowrap flex-shrink-0 ${
              muscleFilter === 'all' ? 'bg-userRed text-bone' : 'bg-graphite/5 text-graphite/60'
            }`}
            data-testid="muscle-chip-all"
          >
            All Groups
          </button>
          {muscleOptions.map((mg) => (
            <button
              key={mg}
              onClick={() => setMuscleFilter(mg)}
              className={`h-8 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold whitespace-nowrap flex-shrink-0 ${
                muscleFilter === mg ? 'bg-userRed text-bone' : 'bg-graphite/5 text-graphite/60'
              }`}
              data-testid={`muscle-chip-${mg}`}
            >
              {mg}
            </button>
          ))}
        </ScrollFadeRow>

        {Object.keys(grouped).length === 0 ? (
          <p className="text-center text-sm text-graphite/50 py-10">No exercises match.</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).map(([mg, exs]) => (
              <div key={mg}>
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-2">
                  {mg} · {exs.length}
                </p>
                <div className="space-y-1.5">
                  {exs.map((ex) => (
                    <button
                      key={ex.id}
                      onClick={() => setSelected(ex)}
                      className="w-full text-left ios-card px-4 py-3 flex items-center gap-3 hover:border-graphite"
                      data-testid={`exercise-list-row-${ex.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-graphite truncate">{ex.name}</p>
                        <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-graphite/50">
                          {ex.equipment_type}
                          {ex.is_custom ? ' · Custom' : ''}
                        </p>
                      </div>
                      {pbs[ex.id] && (
                        <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-userRed bg-userRed/10 px-2 py-1 rounded-ios-pill">
                          <Trophy size={10} weight="fill" />
                          {pbs[ex.id].set_type === 'cardio'
                            ? `${((pbs[ex.id].distance_m || 0) / 1000).toFixed(1)}km`
                            : `${pbs[ex.id].weight}kg`}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCustom && (
        <CustomExerciseModal onCreate={createCustom} onClose={() => setShowCustom(false)} />
      )}
</div>
  );
}

function ExerciseDetail({ exercise, pb, onBack }) {
  const [history, setHistory] = useState([]);
  const [best, setBest] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [loading, setLoading] = useState(true);
  const exerciseId = exercise.id;
  const cardio = exercise.muscle_group === 'Cardio' || (exercise.equipment_type || '').startsWith('Cardio');

  const load = useCallback(async () => {
    try {
      const promises = [
        axios.get(`${API_URL}/exercises/${exerciseId}/history`, { withCredentials: true }),
      ];
      if (cardio) {
        promises.push(axios.get(`${API_URL}/progress/cardio-milestones/${exerciseId}`, { withCredentials: true }));
      }
      const [histRes, mileRes] = await Promise.all(promises);
      setHistory(histRes.data.sets || []);
      setBest(histRes.data.best);
      if (mileRes) setMilestones(mileRes.data.milestones || []);
    } catch (e) {
      console.error('[breth] exercise detail load failed', e);
    } finally { setLoading(false); }
  }, [exerciseId, cardio]);

  useEffect(() => { load(); }, [load]);

  // Group by workout for a session-based history
  const byWorkout = useMemo(() => {
    const arr = [];
    history.forEach((s) => {
      const key = s.workout_id;
      const bucket = arr.find((b) => b.workout_id === key);
      if (bucket) bucket.sets.push(s);
      else arr.push({ workout_id: key, started_at: s.workout_started_at, sets: [s] });
    });
    arr.sort((a, b) => (a.started_at < b.started_at ? 1 : -1));
    arr.forEach((b) => b.sets.sort((x, y) => (x.set_number || 0) - (y.set_number || 0)));
    return arr;
  }, [history]);

  return (
    <div className="mobile-container min-h-screen">
      <div className="content-wrapper px-5 pt-10">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-bold text-graphite/60 hover:text-graphite mb-4"
          data-testid="exercise-detail-back"
        >
          <CaretLeft size={14} weight="bold" /> Back
        </button>

        <div className="mb-5 animate-slide-up">
          <p className="text-xs uppercase tracking-[0.24em] text-graphite/50 font-semibold mb-1">
            {exercise.muscle_group} · {exercise.equipment_type}
          </p>
          <h1 className="text-4xl font-black font-heading uppercase tracking-tight text-graphite leading-none">
            {exercise.name}
          </h1>
        </div>

        {/* Best set card */}
        <div className="ios-card p-4 mb-4 border-userRed/40" data-testid="exercise-best-card">
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={16} weight="fill" className="text-userRed" />
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-graphite/60">
              All-time Best Set
            </p>
          </div>
          {best ? (
            <>
              <p className="text-4xl font-black font-heading tabular-nums text-userRed leading-none">
                {cardio || best.set_type === 'cardio'
                  ? <>{((best.distance_m || 0) / 1000).toFixed(2)}<span className="text-lg font-normal">km · {formatDur(best.duration_seconds)}</span></>
                  : <>{best.weight}<span className="text-lg font-normal">kg</span> × {best.reps}</>}
              </p>
              <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-graphite/50 mt-1.5">
                Set on {format(parseISO(best.logged_at), 'MMM d, yyyy')}
              </p>
            </>
          ) : (
            <p className="text-sm text-graphite/50">
              No sets logged yet — go set the bar.
            </p>
          )}
        </div>

        {/* Goals for this exercise */}
        <GoalsPanel exerciseId={exercise.id} exercises={[exercise]} />

        {/* Cardio milestones */}
        {cardio && milestones && (
          <div className="ios-card p-4 mb-4" data-testid="cardio-milestones-card">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={16} weight="fill" className="text-partnerBlue" />
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-graphite/60">
                Distance Milestones
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {milestones.map((m) => (
                <div
                  key={m.distance_m}
                  className={`rounded-ios p-3 text-center ${m.best_seconds ? 'bg-partnerBlue/6 border border-partnerBlue/25' : 'bg-graphite/[0.04] border border-hairline'}`}
                  data-testid={`milestone-${m.distance_m}`}
                >
                  <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 leading-none">
                    {m.distance_m === 1000 ? '1K' : m.distance_m === 2000 ? '2K' : '5K'}
                  </p>
                  {m.best_seconds ? (
                    <>
                      <p className="text-2xl font-black font-heading tabular-nums text-partnerBlue mt-1 leading-none">
                        {fmtMinSec(m.best_seconds)}
                      </p>
                      <p className="text-[9px] uppercase tracking-[0.15em] font-bold text-graphite/50 mt-1 leading-none">
                        {m.logged_at && format(parseISO(m.logged_at), 'MMM d')}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-graphite/40 mt-2 italic">Not yet</p>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-graphite/40 mt-3">
              Projected times based on your best constant-pace effort
            </p>
          </div>
        )}

        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-2 px-1">
          Session History
        </p>

        {loading ? (
          <p className="text-sm text-graphite/40 text-center py-6">Loading…</p>
        ) : byWorkout.length === 0 ? (
          <div className="ios-card p-6 text-center">
            <p className="text-sm text-graphite/50">
              You haven't logged this exercise yet.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {byWorkout.map((b) => (
              <div key={b.workout_id} className="ios-card p-4" data-testid={`session-history-${b.workout_id}`}>
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-2">
                  {b.started_at && format(parseISO(b.started_at), 'EEE MMM d, yyyy · h:mm a')}
                </p>
                <div className="space-y-1">
                  {b.sets.map((s, idx) => (
                    <div
                      key={s.id || idx}
                      className={`flex items-center justify-between text-sm py-1 px-2 rounded-ios ${s.is_best ? 'bg-userRed/8' : ''}`}
                    >
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-graphite/50 w-6">#{s.set_number || idx + 1}</span>
                      <div className="flex-1 font-bold text-graphite tabular-nums">
                        {cardio || s.set_type === 'cardio'
                          ? <>{((s.distance_m || 0) / 1000).toFixed(2)}<span className="text-xs font-normal">km · {formatDur(s.duration_seconds)}</span></>
                          : <>{s.weight}<span className="text-xs font-normal">kg</span> × {s.reps}</>}
                      </div>
                      {s.rpe && <span className="text-[10px] font-bold text-graphite/50 tabular-nums">RPE {s.rpe}</span>}
                      {s.is_best && <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-bone bg-userRed px-1.5 py-0.5 rounded-ios-pill ml-2">Best</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
</div>
  );
}

function CustomExerciseModal({ onCreate, onClose }) {
  const [name, setName] = useState('');
  const [muscleGroup, setMuscleGroup] = useState(MUSCLE_GROUPS[0]);
  const [equipmentType, setEquipmentType] = useState(EQUIPMENT_TYPES[0]);

  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up max-h-[85vh] flex flex-col">
        <div className="px-5 py-5 overflow-y-auto space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black font-heading uppercase text-graphite">Custom Exercise</h2>
            <button onClick={onClose} className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center" data-testid="close-custom-button">
              <X size={18} weight="bold" />
            </button>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-12 px-4 rounded-ios-pill border border-hairline bg-boneWarm text-base"
              placeholder="e.g. Landmine Press"
              data-testid="custom-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">Type</label>
            <div className="flex gap-1.5 flex-wrap">
              {EQUIPMENT_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setEquipmentType(t)}
                  className={`h-9 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold ${
                    equipmentType === t ? 'bg-graphite text-bone' : 'bg-graphite/5 text-graphite/60'
                  }`}
                  data-testid={`custom-type-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">Muscle Group</label>
            <div className="flex gap-1.5 flex-wrap">
              {MUSCLE_GROUPS.map((m) => (
                <button
                  key={m}
                  onClick={() => setMuscleGroup(m)}
                  className={`h-9 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold ${
                    muscleGroup === m ? 'bg-userRed text-bone' : 'bg-graphite/5 text-graphite/60'
                  }`}
                  data-testid={`custom-muscle-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => name.trim() && onCreate({ name: name.trim(), muscle_group: muscleGroup, equipment_type: equipmentType })}
            disabled={!name.trim()}
            className="w-full h-14 bg-userRed text-bone font-black text-sm uppercase tracking-[0.15em] rounded-ios-pill disabled:opacity-40"
            data-testid="create-custom-button"
          >
            Add Exercise
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDur(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
}

function fmtMinSec(secs) {
  if (secs == null) return '—';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
